import type { Segno } from '../types'
import { roundTenth } from './segmentQueue'

export type SegnoEditFailure =
  | 'SHIFT_BEFORE_ZERO'
  | 'NO_PREVIOUS'
  | 'NO_NEXT'
  | 'SPLIT_OUTSIDE'

export type SegnoEditResult =
  | { ok: true; segnos: Segno[] }
  | { ok: false; reason: SegnoEditFailure }

const MIN_SEGNO_DURATION_SECONDS = 0.1
const EPSILON = 0.000001

function ordered(segnos: Segno[]): Segno[] {
  return [...segnos].sort((left, right) => left.order - right.order)
}

function renumber(segnos: Segno[]): Segno[] {
  return segnos.map((segno, index) => ({ ...segno, order: index + 1 }))
}

function combineDistinct(left: string | null | undefined, right: string | null | undefined, separator: string): string {
  const first = (left ?? '').trim()
  const second = (right ?? '').trim()
  if (!first) return second
  if (!second || first === second) return first
  return `${first}${separator}${second}`
}

/**
 * 모든 세뇨의 시작/끝을 같은 양만큼 옮깁니다.
 * 한 세뇨라도 0초 이전으로 이동해야 한다면 전체 이동을 거부하여
 * 세뇨 사이의 상대 간격이 깨지지 않도록 합니다.
 */
export function shiftAllSegnoTimes(segnos: Segno[], delta: -0.1 | 0.1): SegnoEditResult {
  if (segnos.some((segno) => segno.startTime + delta < -EPSILON)) {
    return { ok: false, reason: 'SHIFT_BEFORE_ZERO' }
  }

  return {
    ok: true,
    segnos: segnos.map((segno) => ({
      ...segno,
      startTime: roundTenth(segno.startTime + delta),
      endTime: roundTenth(segno.endTime + delta),
    })),
  }
}

/**
 * 현재 세뇨를 order 기준 이전/다음 세뇨와 합칩니다.
 * 시간 범위는 두 세뇨를 모두 포함하도록 확장하고,
 * 사용자가 작성한 제목/가사는 잃지 않도록 서로 다를 때 함께 보존합니다.
 */
export function mergeSegnoWithNeighbor(
  segnos: Segno[],
  order: number,
  direction: 'previous' | 'next',
): SegnoEditResult {
  const list = ordered(segnos)
  const currentIndex = list.findIndex((segno) => segno.order === order)
  if (currentIndex < 0) {
    return { ok: false, reason: direction === 'previous' ? 'NO_PREVIOUS' : 'NO_NEXT' }
  }

  const neighborIndex = direction === 'previous' ? currentIndex - 1 : currentIndex + 1
  if (neighborIndex < 0) return { ok: false, reason: 'NO_PREVIOUS' }
  if (neighborIndex >= list.length) return { ok: false, reason: 'NO_NEXT' }

  const firstIndex = Math.min(currentIndex, neighborIndex)
  const secondIndex = Math.max(currentIndex, neighborIndex)
  const first = list[firstIndex]
  const second = list[secondIndex]

  const merged: Segno = {
    ...first,
    title: combineDistinct(first.title, second.title, ' / '),
    startTime: roundTenth(Math.min(first.startTime, second.startTime)),
    endTime: roundTenth(Math.max(first.endTime, second.endTime)),
    lyrics: combineDistinct(first.lyrics, second.lyrics, '\n'),
  }

  return {
    ok: true,
    segnos: renumber([
      ...list.slice(0, firstIndex),
      merged,
      ...list.slice(secondIndex + 1),
    ]),
  }
}

/**
 * 현재 재생 위치를 기준으로 하나의 세뇨를 둘로 나눕니다.
 * 0.1초 단위 편집 규칙에 맞춰 양쪽 구간 모두 최소 0.1초를 확보해야 합니다.
 * 제목과 가사는 두 구간에 복사해 자동 편집으로 사용자 입력이 사라지지 않게 합니다.
 */
export function splitSegnoAtTime(
  segnos: Segno[],
  order: number,
  splitTime: number,
): SegnoEditResult {
  const list = ordered(segnos)
  const index = list.findIndex((segno) => segno.order === order)
  if (index < 0) return { ok: false, reason: 'SPLIT_OUTSIDE' }

  const current = list[index]
  const split = roundTenth(splitTime)
  const minimum = roundTenth(current.startTime + MIN_SEGNO_DURATION_SECONDS)
  const maximum = roundTenth(current.endTime - MIN_SEGNO_DURATION_SECONDS)

  if (split < minimum - EPSILON || split > maximum + EPSILON) {
    return { ok: false, reason: 'SPLIT_OUTSIDE' }
  }

  // 기존 서버 id를 두 새 구간에 중복 복사하지 않습니다.
  // 저장 payload는 order/title/start/end/lyrics로 다시 구성되므로 편집 계약은 유지됩니다.
  const { id: _id, ...editable } = current

  const before: Segno = {
    ...editable,
    endTime: split,
  }
  const after: Segno = {
    ...editable,
    startTime: split,
  }

  return {
    ok: true,
    segnos: renumber([
      ...list.slice(0, index),
      before,
      after,
      ...list.slice(index + 1),
    ]),
  }
}
